import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { Guid } from 'guid-typescript';
import {
    Cart,
    AddOrUpdateCartItem,
    ReorderCart,
    ProductPriceStockRequest,
    ProductPriceStock,
    ProductAvailableResponse,
    JeevesPriceRequest,
    JeevesPriceResponse
} from '@/models/Cart/Cart';
import { SearchResult } from '@/models/QuickSearchModel';
import { CheckoutModel, CampaignModel } from '@/models/Cart/Checkout';
import { CheckoutQuery } from '@/models/Cart/Unifaun';
function errorResponse(e: any): Promise<AxiosResponse<any>> {
    const response: AxiosResponse<boolean> = {
        data: false,
        status: e.response.status,
        statusText: e.response.data,
        headers: {},
        request: {},
        config: (null as unknown) as AxiosRequestConfig
    };
    return Promise.resolve(response);
}

export const ApiService = {
    async getSearchResult(q: string): Promise<AxiosResponse<SearchResult['items']>> {
        try {
            return await axios.post('/api/quickSearch', JSON.stringify(q), {
                headers: {
                    'Content-Type': 'application/json',
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async checkoutOrder(model: CheckoutModel): Promise<AxiosResponse<CheckoutModel>> {
        try {
            return await axios.post('/api/checkout/placeorder', model, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async setDelivery(model: CheckoutModel): Promise<AxiosResponse<CheckoutModel>> {
        try {
            return await axios.put('/api/checkout/setDeliveryProvider', model, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async setPaymentProvider(model: CheckoutModel): Promise<AxiosResponse<CheckoutModel>> {
        try {
            return await axios.put('/api/checkout/setPaymentProvider', model, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async setShippingOption(model: CheckoutModel): Promise<AxiosResponse<CheckoutModel>> {
        try {
            return await axios.put('/api/klarnacheckout/setShippingOption', model, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async setDeliveryProvider(model: CheckoutModel): Promise<AxiosResponse<CheckoutModel>> {
        try {
            return await axios.put('/api/checkout/setDeliveryProvider', model, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async setCampaignCode(campaignModel: CampaignModel): Promise<AxiosResponse<CampaignModel>> {
        try {
            return await axios.put('/api/checkout/setCampaignCode', campaignModel, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getMiniCart(): Promise<AxiosResponse<Cart>> {
        try {
            return await axios.get('/api/cart', {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext)
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async addToCart(cartItem: AddOrUpdateCartItem): Promise<AxiosResponse<Cart>> {
        try {
            return await axios.post('/api/cart/add', cartItem, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async addListToCart(cartItem: Array<AddOrUpdateCartItem>): Promise<AxiosResponse<Cart>> {
        try {
            return await axios.post('/api/cart/addconfiguration', cartItem, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async updateCart(cartItem: AddOrUpdateCartItem): Promise<AxiosResponse<Cart>> {
        try {
            return await axios.put('/api/cart/update', cartItem, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async reorderCart(cartItem: ReorderCart): Promise<AxiosResponse<Cart>> {
        try {
            return await axios.post('/api/cart/reorder', cartItem, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async placeOrder(cartItem: ReorderCart): Promise<AxiosResponse<Cart>> {
        try {
            return await axios.post('/api/checkout/placeorder', cartItem, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getProductPriceStock(request: ProductPriceStockRequest): Promise<AxiosResponse<ProductPriceStock>> {
        try {
            return await axios.post('/api/cart/productpricestock', request, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext)
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getProductDeliveryDate(request: ProductPriceStockRequest): Promise<AxiosResponse<ProductAvailableResponse>> {
        try {
            return await axios.post('/api/cart/productdeliverydate', request, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext)
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getDeliveryCheckouts(model: CheckoutQuery): Promise<AxiosResponse<any>> {
        try {
            return await axios.post('/transportapi/unifaun/getDeliveryCheckouts', model, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext)
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getCustomercallsReport(dataIdentifier: string, fromDate: Date, toDate: Date): Promise<AxiosResponse<any>> {
        try {
            return await axios.get('/api/customerservice/getcustomerservicereport', {
                params: {
                    dataIdentifier,
                    fromDate,
                    toDate
                },
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext)
                },
                responseType: 'arraybuffer'
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async importExcelToCheckout(files: File): Promise<AxiosResponse<any>> {
        try {
            return await axios.post('/api/cart/importExcelToCheckout', files, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getSampleCheckoutExcelFile(): Promise<AxiosResponse<any>> {
        try {
            return await axios.get('/api/cart/getSampleCheckoutExcelFile', {
                responseType: 'arraybuffer',
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    RequestVerificationToken: window.localStorage.getItem('requestVerificationToken')
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async getJeevesPrices(request: JeevesPriceRequest): Promise<AxiosResponse<JeevesPriceResponse>> {
        try {
            return await axios.post('/api/cart/getjeevesprice', request, {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext)
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    },
    async selectCountry(selectedCountryId: string): Promise<AxiosResponse<any>> {
        try {
            return await axios.post('/api/countrypicker/post', '"' + selectedCountryId + '"', {
                headers: {
                    'litium-request-context': JSON.stringify(window.__litium.requestContext),
                    'Content-Type': 'application/json'
                }
            });
        } catch (e) {
            return errorResponse(e);
        }
    }
};
